import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoutes";
import Sidebar from "./components/Navs/Sidebar";
import Header from "./components/Navs/Header";

// Role Helpers
import {
  isCourierOrAdmin,
  canAccessCourierCompanyRoutes,
  canViewStandaloneTransactionDetail,
  isInvestorSuperOrOpp,
  isSuperAdmin,
  isSuperAdminOrOpp,
} from "./helpers";

// Public Pages
import LoginPage from "./pages/auth/Login";
import ForgotPasswordPage from "./pages/auth/ForgotPasswprd";
import ForgotPasswordOTP from "./pages/auth/ForgotPasswordOTP";
import ResetPasswordPage from "./pages/auth/ResetPassword";
import PasswordResetSuccess from "./pages/auth/PasswordResetSuccess";

// Protected Pages
import Layout from "./components/Layout";
import Dashboard from "./pages/private/dashboard";

// User Management
import UserManagement from "./pages/private/user-management";
import Vendor from "./pages/private/user-management/Vendor";
import VendorDetails from "./pages/private/user-management/Vendor/VendorDetails";
import Buyer from "./pages/private/user-management/Buyer";
import BuyerDetails from "./pages/private/user-management/Buyer/BuyerDetails";
import Referred from "./pages/private/user-management/Referred";
import ViewRefferdUserTransactions from "./pages/private/user-management/Referred/ViewRefferedUserTransactions";

// Order Management
import OrderManagement from "./pages/private/order-management";
import OrderDetailsPage from "./pages/private/order-management/ViewOrderDetails";
import DisputedOrders from "./pages/private/order-management/DisputedOrders";
import DisputedOrderDetails from "./pages/private/order-management/ViewDisputedOrderDetails";
import ReturnedOrders from "./pages/private/order-management/ReturnedOrders";
import ReturnedOrderDetails from "./pages/private/order-management/ReturnedOrders/ViewReturnedOrderDetails";
import ExecutedOrders from "./pages/private/order-management/ExecutedOrders";
import OrdersInTransit from "./pages/private/order-management/OrdersInTransit";

// Courier & Agent Management
import CourierManagement from "./pages/private/courier-management";
import CourierCompany from "./pages/private/courier-management/CourierCompany";
import AgentManagement from "./pages/private/agent-management";

// Admin & Super Admin
import ContractManagement from "./pages/private/contract-management";
import ViewPublicContractDetails from "./pages/private/contract-management/ViewPublicContractDetails";
import AdminManagement from "./pages/private/admin-management";
import RequestManagement from "./pages/private/RequestManagement";
import RequestDetails from "./pages/private/RequestManagement/ViewRequestDetails";
import KYCManagement from "./pages/private/Kyc";
import Coupons from "./pages/private/Coupons";

// Analytics & Transactions
import Analytics from "./pages/private/Analytics";
import DetailedAnalytics from "./pages/private/Analytics/DetailedAnalytics";
import TransactionsTable from "./pages/private/Analytics/TransactionsTable";
import TransactionDetails from "./pages/private/Analytics/TransactionDetails";

// General
import Settings from "./pages/private/settings";
import PaymentMethods from "./pages/private/settings/PaymentMethods";
import NotFound from "./routes/NotFoundPage";

// Redux
import { useAppDispatch, useAppSelector } from "./redux/app/hooks";
import { RootState } from "./redux/app/store";
import {
  fetchAllCountries,
  fetchAllLanguages,
} from "./redux/features/utilSlice";
import {
  fetchAdminProfileAsync,
  fetchRolesAsync,
} from "./redux/features/adminSlice";
import { logout, updateTokens } from "./redux/features/authSlice";
import Notifications from "./pages/private/Notifications";
import ProductManagement from "./pages/private/product-management";
import ViewProductDetails from "./pages/private/product-management/ViewProductDetails";
import ViewBagDetails from "./pages/private/RequestManagement/ViewBagDetails";

// Token Manager
import { tokenManager } from "./utils/tokenManager";
import { isTokenExpired } from "./utils/tokenUtils";

/** Keep old /contract-management bookmarks working. */
const LegacyContractManagementRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/invoice-management/${id}`} replace />;
};

const App = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(
    (state: RootState) => state.auth.isAuthenticated,
  );
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    // Set up tokenManager event listeners
    const handleTokenEvent = (event: any) => {
      if (event.type === "TOKEN_REFRESH_SUCCESS") {
        // Update Redux store with new tokens
        dispatch(updateTokens(event.tokens));
      } else if (event.type === "TOKEN_REFRESH_FAILED") {
        // Set flag for login page to show toast
        sessionStorage.setItem("showSessionExpiredToast", "true");
        // Logout user when token refresh fails
        dispatch(logout());
      }
    };

    tokenManager.addEventListener(handleTokenEvent);

    if (isAuthenticated) {
      // Check if token is expired on app load
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const accessToken = user?.access_token;
      if (accessToken && isTokenExpired(accessToken)) {
        // Set flag for login page to show toast (use sessionStorage to avoid clearing)
        sessionStorage.setItem("showSessionExpiredToast", "true");
        // Token is expired, immediate redirect to login
        localStorage.clear();
        window.location.href = "/";
        return;
      }

      dispatch(fetchAllCountries());
      dispatch(fetchAllLanguages());
      dispatch(fetchAdminProfileAsync());
      dispatch(fetchRolesAsync());
      // Initialize token manager for automatic refresh
      tokenManager.initialize();
    } else {
      // Clear token manager when not authenticated
      tokenManager.clearTimer();
    }

    // Cleanup event listener on unmount
    return () => {
      tokenManager.removeEventListener(handleTokenEvent);
    };
  }, [dispatch, isAuthenticated]);

  // Handle login/logout events
  useEffect(() => {
    if (isAuthenticated) {
      tokenManager.onLogin();
    } else {
      tokenManager.onLogout();
    }
  }, [isAuthenticated]);

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/forgot-password/verify-otp"
          element={<ForgotPasswordOTP />}
        />
        <Route
          path="/forgot-password/success"
          element={<PasswordResetSuccess />}
        />

        {/* Protected Routes */}
        <Route
          element={
            <ProtectedRoute>
              <Layout>
                <Sidebar />
                <Header />
              </Layout>
            </ProtectedRoute>
          }
        >
          {/* Dashboard & User Management */}
          {isInvestorSuperOrOpp(user) && (
            <>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/user-management" element={<UserManagement />} />
              <Route path="/user-management/vendor" element={<Vendor />} />
              <Route
                path="/user-management/vendor/:id"
                element={<VendorDetails />}
              />
              <Route path="/user-management/buyer" element={<Buyer />} />
              <Route
                path="/user-management/buyer/:id"
                element={<BuyerDetails />}
              />
              <Route path="/user-management/referred" element={<Referred />} />
              <Route
                path="/user-management/referred/transactions"
                element={<ViewRefferdUserTransactions />}
              />
            </>
          )}

          {/* Courier & Company Management */}
          {canAccessCourierCompanyRoutes(user) && (
            <>
              <Route
                path="/courier-management"
                element={<CourierManagement />}
              />
              <Route
                path="/courier-management/:id"
                element={<CourierCompany />}
              />
            </>
          )}

          {/* Orders & Contracts */}
          {isCourierOrAdmin(user) && (
            <>
              <Route
                path="/order-management/all-orders"
                element={<OrderManagement />}
              />
              <Route
                path="/order-management/disputed-orders"
                element={<DisputedOrders />}
              />
              <Route
                path="/order-management/returned-orders"
                element={<ReturnedOrders />}
              />
              <Route
                path="/order-management/executed-orders"
                element={<ExecutedOrders />}
              />
              <Route
                path="/order-management/orders-in-transit"
                element={<OrdersInTransit />}
              />
              <Route path="/order-details/:id" element={<OrderDetailsPage />} />
              <Route
                path="/returned-order-details/:id"
                element={<ReturnedOrderDetails />}
              />
              <Route
                path="/dispute-order-details/:id"
                element={<DisputedOrderDetails />}
              />
              <Route path="/agent-management" element={<AgentManagement />} />
            </>
          )}

          {/* Investor & Super Admin */}
          {isInvestorSuperOrOpp(user) && (
            <>
              <Route
                path="/request-management"
                element={<RequestManagement />}
              />
              <Route
                path="/request-management/request-details/:id"
                element={<RequestDetails />}
              />
              <Route path="/active-bags/:id" element={<ViewBagDetails />} />
            </>
          )}

          {isSuperAdmin(user) && (
            <>
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/transactions" element={<TransactionsTable />} />
              <Route
                path="/detailed-analytics"
                element={<DetailedAnalytics />}
              />
            </>
          )}

          {canViewStandaloneTransactionDetail(user) && (
            <Route
              path="/transactions/transaction-details/:id"
              element={<TransactionDetails />}
            />
          )}

          {isSuperAdminOrOpp(user) && (
            <>
              <Route path="/kyc-management" element={<KYCManagement />} />
              <Route path="/coupons" element={<Coupons />} />
              <Route
                path="/product-management"
                element={<ProductManagement />}
              />
              <Route
                path="/products/details/:id"
                element={<ViewProductDetails />}
              />
              <Route
                path="/invoice-management"
                element={<ContractManagement />}
              />
              <Route
                path="/invoice-management/:id"
                element={<ViewPublicContractDetails />}
              />
              {/* Legacy URLs from earlier naming */}
              <Route
                path="/contract-management"
                element={<Navigate to="/invoice-management" replace />}
              />
              <Route
                path="/contract-management/:id"
                element={<LegacyContractManagementRedirect />}
              />
            </>
          )}

          {isSuperAdmin(user) && (
            <>
              <Route path="/admin-management" element={<AdminManagement />} />
              <Route path="/notifications" element={<Notifications />} />
            </>
          )}

          {/* Settings (everyone authenticated) */}
          <Route path="/settings" element={<Settings />} />

          {/* Payment Methods (super admin only) */}
          {isSuperAdmin(user) && (
            <Route
              path="/settings/payment-methods"
              element={<PaymentMethods />}
            />
          )}
        </Route>

        {/* 404 Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
};

export default App;
